import React, { useEffect, Suspense, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useSelector, useDispatch } from "react-redux";
import { selectIsAuthenticated } from "@features/DiscordAuth/model/selectors";
import { useGetUserInfoQuery } from "@entities/User/api/UserInformationSlice";
import { setUserInfo } from "@entities/User/model/slice";
import { logout } from "@features/DiscordAuth/model/authSlice";
import styles from "../styles/LobbyPage.module.css";
import { SuspenseLoader } from "@shared/ui/Loader";
import ModalTutorial from "@widgets/Tutorial";// Lazy загрузка компонентов
const GamesMenuHeader = React.lazy(() => import("@widgets/GamesMenuHeader/ui"));
const ProfileHeaderCard = React.lazy(
  () => import("@widgets/ProfileHeaderCard/ui")
);
const LeaderBoard = React.lazy(() => import("@widgets/LeaderBoard/ui"));
const NavigationsTabs = React.lazy(() => import("@widgets/NavigationsTabs/ui"));
const GoOnButton = React.lazy(() => import("@widgets/GoOnButton/ui"));
const MainPageBG = React.lazy(() => import("@shared/ui/BackGround/Main"));

const LobbyPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  
  const [leaderboardLoaded, setLeaderboardLoaded] = useState(false);
  const [retry, setRetry] = useState(false);

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetUserInfoQuery(undefined, { pollingInterval: 30000 });

  // Обработчик изменения состояния загрузки лидерборда
  const handleLeaderboardLoadingChange = (isLoaded: boolean) => {
    console.log("Leaderboard loaded:", isLoaded);
    setLeaderboardLoaded(isLoaded);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: "/" });
    }
  }, [isAuthenticated, navigate]);

  // Обновляем Redux-стейт пользователя
  useEffect(() => {
    if (user) {
      dispatch(setUserInfo(user));
    }
  }, [user, dispatch]);

  // Обрабатываем случай, когда user не пришел
  useEffect(() => {
    if (!isLoading && !user && !error && !retry) {
      console.warn("User не получен, пробуем повторный запрос...");
      setRetry(true);
      setTimeout(() => {
        refetch(); // Повторяем запрос
        setRetry(false);
      }, 2000);
    }
  }, [user, isLoading, error, refetch, retry]);

  // Обработка ошибок API-запроса
  useEffect(() => {
    if (error && "status" in error) {
      if (error.status === 404) {
        window.location.href = "https://discord.gg/Epv3C8Ru";
      } else if (error.status === 401 || error.status === 403) {
        dispatch(logout());
        navigate({ to: "/" });
      }
    }
  }, [error, dispatch, navigate]);

  // Показываем загрузчик, пока идет запрос данных пользователя или не загрузилась лидерборда
  if (isLoading && !leaderboardLoaded) {
    return <SuspenseLoader />;
  }

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <div className={styles.background}>
        <MainPageBG />
      </div>
      <div className={styles.bode}>
        <div className={styles.container}>
          <div className={styles.topRow}>
            <div className={`${styles.leftProfile}`}>
              <ProfileHeaderCard />
            </div>
            <div className={styles.centerHeader}>
              <GamesMenuHeader />
            </div>
            <div className={`${styles.rightLeaderboard}`}>
              <LeaderBoard onLoadingChange={handleLeaderboardLoadingChange} />
            </div>
          </div>

          <div className={styles.middle}></div>

          <div className={styles.bottomRow}>
            <div className={`${styles.tabs}`}>
              <NavigationsTabs />
            </div>
            <div className={`${styles.goOnButton}`}>
              <GoOnButton />
            </div>
            <div className={styles.modalTutorial}>
              <ModalTutorial />
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
};

export default LobbyPage;
